import type { CalendarItem } from '@efdigital/ll-addon-data';
import { queryCalendarItems } from '@efdigital/ll-admin-v2/data/builders/xceed/calendar-items';
import { fn } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import CalendarTable from '../programs/calendar/calendar-table.gts';

type Signature = {
  Args: {
    programId: string;
    cohortId: string;
    onSelect: (calendarItems: CalendarItem[]) => void;
  };
  Blocks: {
    input: [showModal: (selectedCalendarItemIds: string[]) => void];
  };
};

export default class CalendarItemPicker extends Component<Signature> {
  @tracked isOpen = false;
  @service declare store: Store;

  @tracked selectedCalendarItemIds: string[] = [];

  showModal = (selectedCalendarItemIds: string[]) => {
    this.selectedCalendarItemIds = [...selectedCalendarItemIds];
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  selectCalendarItem = (calendarItem: CalendarItem, event: Event) => {
    event.stopPropagation();
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedCalendarItemIds = [
        ...this.selectedCalendarItemIds,
        calendarItem.id!,
      ];
    } else {
      this.selectedCalendarItemIds = this.selectedCalendarItemIds.filter(
        (id) => id !== calendarItem.id!
      );
    }
  };

  confirmSelection = (calendarItems: CalendarItem[]): void => {
    const foundSelectedCalendarItems = this.selectedCalendarItemIds
      .map((id) => calendarItems.find((item) => item.id === id))
      .filter((item): item is CalendarItem => item !== undefined);
    this.args.onSelect(foundSelectedCalendarItems);
    this.isOpen = false;
  };

  isSelected = (calendarItem: CalendarItem): boolean => {
    return this.selectedCalendarItemIds.includes(calendarItem.id!);
  };

  <template>
    {{yield this.showModal to="input"}}
    {{#let (uniqueId) as |uniqueId|}}
      {{#if this.isOpen}}
        <Request @query={{queryCalendarItems @programId @cohortId}}>
          <:content as |calendarItems|>
            <BsModal
              @open={{this.isOpen}}
              @onHidden={{this.modalClosed}}
              @size="xl"
              @renderInPlace={{true}}
              @backdropClose={{false}}
              as |modal|
            >
              <modal.header>
                <h2 class="h4 modal-title">Events</h2>
              </modal.header>
              <modal.body>
                <CalendarTable @calendarItems={{calendarItems.data}}>
                  <:title as |calendarItem|>
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="{{uniqueId}}-{{calendarItem.id}}"
                      checked={{this.isSelected calendarItem}}
                      {{on "change" (fn this.selectCalendarItem calendarItem)}}
                    />
                    <label
                      class="form-check-label"
                      for="{{uniqueId}}-{{calendarItem.id}}"
                    >
                      {{calendarItem.title}}
                    </label>
                  </:title>
                </CalendarTable>
              </modal.body>
              <modal.footer class="text-center">
                Selected
                {{this.selectedCalendarItemIds.length}}
                of
                {{calendarItems.data.length}}
                <button
                  type="button"
                  class="btn btn-primary btn-md"
                  {{on "click" (fn this.confirmSelection calendarItems.data)}}
                >Select</button>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-md"
                  {{on "click" this.modalClosed}}
                >Cancel</button>
              </modal.footer>
            </BsModal>
          </:content>
          <:error as |error|>
            <p>Error: {{error.message}}</p>
          </:error>
        </Request>
      {{/if}}
    {{/let}}
  </template>
}
